import {
  takeLatest, put, call, select
} from 'redux-saga/effects';
import {
  keys, prop, groupBy, compose, reduce, map, assoc
} from 'ramda';
import dayjs from 'dayjs';
import toastrError from 'src/helpers/toastr/toastrError';
import toastrSuccess from 'src/helpers/toastr/toastrSuccess';
import {
  lawsActions, paragraphsActions, userParagrapghsActions,
  userParagraphActions, getParagraphFormValues, getLawId, getParagraph
} from 'src/store/reducers/lawReducer';

import { wtsApi } from 'src/api';
import Utils from 'src/utils';
import { getFieldsConfig } from 'src/containers/Settings/Law/paragraphConfig';

// eslint-disable-next-line func-names
export default function* () {
  yield [
    takeLatest(lawsActions.request, lawsRequestSaga),
    takeLatest(paragraphsActions.activeChange, onParagraphChange),
    takeLatest(userParagraphActions.update.request, userParagraphUpdate),
    takeLatest(userParagraphActions.delete.request, userParagraphDelete)
  ];
}

const normalize = (list) => list.reduce((acc, item) => {
  acc[item.id] = item;
  return acc;
}, {});

const parseConfig = ({
  configuration,
  ...other
}) => ({
  ...other,
  configuration: JSON.parse(configuration)
});

export const parseLawConfig = (rawResponse) => {
  const decapitalized = Utils.objects.decapitalizeObject(rawResponse);
  return parseConfig(decapitalized);
};

const parseUserParagraph = ({ configuration, ...item }) => {
  const {
    MinutesBetweenEvents,
    ContractTypeIds,
    ...other
  } = configuration;

  return {
    ...item,
    configuration: {
      ...other,
      MinutesBetweenEvents: MinutesBetweenEvents ? MinutesBetweenEvents / 60 : MinutesBetweenEvents,
      ContractTypeIds: ContractTypeIds ? ContractTypeIds.map((id) => id.toString()) : ContractTypeIds
    }
  };
};

function* lawsRequestSaga() {
  try {
    // get laws
    const lawsListResponse = yield call(wtsApi.laws.lawsList);
    const lawsList = lawsListResponse.data.Data.map(Utils.objects.decapitalizeObject);
    const laws = normalize(lawsList);
    const lawIds = keys(laws);
    yield put({ type: lawsActions.success, payload: laws });

    // get paragraphs
    const paragraphsResponse = yield call(wtsApi.lawParagraphs.lawParagraphsList, { lawIds });
    const paragraphs = paragraphsResponse.data.Data.map(Utils.objects.decapitalizeObject);
    const groupedParagraphs = compose(map(normalize), groupBy(prop('lawId')))(paragraphs);
    yield put({ type: paragraphsActions.success, payload: groupedParagraphs });

    // get user paragraphs
    const lawIdByParId = reduce((acc, { id, lawId }) => assoc(id, lawId, acc), {}, paragraphs);
    const userParagraphsResponse = yield call(wtsApi.lawParagraphConfs.lawParagraphConfsList, { lawIds });
    const userParagraphs = userParagraphsResponse.data.Data
      .map(Utils.objects.decapitalizeObject)
      .map(parseConfig);
    const groupedUserParagraphs = compose(
      map(normalize),
      groupBy(prop('lawId')),
      map((item) => assoc('lawId', lawIdByParId[item.paragraphId], item)),
      map(parseUserParagraph)
    )(userParagraphs);

    yield put({ type: userParagrapghsActions.success, payload: groupedUserParagraphs });
    yield loadParagraphOptions();
  } catch (e) {
    yield put({ type: lawsActions.fail, payload: e });
    toastrError(e);
  }
}

const toOptions = ({ id, name }) => ({ label: name, value: id.toString() });

function* requestJobs() {
  try {
    const jobsResponse = yield call(wtsApi.jobs.jobsList);
    const jobs = jobsResponse.data.Data.filter(({ IsLeave }) => !IsLeave).map(({ JobId, JobName }) => ({ id: JobId, name: JobName }));
    return map(toOptions, jobs);
  } catch (e) {
    toastrError(e);
    return undefined;
  }
}

function* requestContractTypes() {
  try {
    const contractTypesResponse = yield call(wtsApi.contractTypes.contractTypesList);
    const contractTypes = contractTypesResponse.data.Data.map(
      ({ ContractTypeId, ContractTypeName }) => ({ id: ContractTypeId, name: ContractTypeName })
    );
    return map(toOptions, contractTypes);
  } catch (e) {
    toastrError(e);
    return undefined;
  }
}

function* requestTimeRateSystems() {
  try {
    const timeRateSystemsResponse = yield call(wtsApi.timeRateSystems.timeRateSystemsList);
    const timeRateSystems = timeRateSystemsResponse.data.Data.map(({ Id, Name }) => ({ id: Id, name: Name }));
    return map(toOptions, timeRateSystems);
  } catch (e) {
    toastrError(e);
    return undefined;
  }
}

function* loadParagraphOptions() {
  const lawId = yield select(getLawId);
  const paragraph = yield select(getParagraph);
  const paragraphConfig = getFieldsConfig(paragraph.name);

  const paragraphOptions = paragraphConfig.reduce((counter, { optionsPath }) => (optionsPath ? [...counter, ...optionsPath] : counter), []);

  if (lawId && Object.entries(paragraph).length) {
    const options = {};
    if (paragraphOptions.includes('contractTypes')) {
      options.contractTypes = yield* requestContractTypes();
    }
    if (paragraphOptions.includes('timeRateSystems')) {
      options.timeRateSystems = yield* requestTimeRateSystems();
    }
    if (paragraphOptions.includes('jobs')) {
      options.jobs = yield* requestJobs();
    }

    yield put({
      type: paragraphsActions.optionsSuccess,
      payload: {
        lawId: lawId.toString(),
        paragraphId: paragraph.id,
        options
      }
    });
  }
}

function* onParagraphChange() {
  try {
    // Load paragraph dropdowns options
    yield loadParagraphOptions();
  } catch (e) {
    console.error(e);
  }
}

function* userParagraphUpdate() {
  try {
    const paragraph = yield select(getParagraphFormValues);
    const data = prepareRequestParams(paragraph);
    const { id } = paragraph;

    // update or create, depends on id param
    const apiCall = id
      ? wtsApi.lawParagraphConfs.lawParagraphConfsUpdate
      : wtsApi.lawParagraphConfs.lawParagraphConfsCreate;
    const type = id ? userParagraphActions.update.success : userParagraphActions.create.success;

    const respRawResponse = yield call(apiCall, data);
    const respRaw = respRawResponse.data;
    const resp = parseLawConfig(respRaw.Data);
    const payload = { ...paragraph, ...parseUserParagraph(resp) }; // align response format with front data structure

    toastrSuccess(respRaw);

    yield put({ type, payload });
  } catch (e) {
    toastrError(e);
  }
}
function* userParagraphDelete() {
  try {
    const paragraph = yield select(getParagraphFormValues);
    const { id } = paragraph;
    const response = yield call(wtsApi.lawParagraphConfs.lawParagraphConfsDelete, { id });
    yield put({
      type: userParagraphActions.delete.success,
      payload: paragraph
    });

    toastrSuccess(response);
  } catch (e) {
    toastrError(e);
  }
}

const prepareRequestParams = ({
  id: Id,
  paragraphId: ParagraphId,
  name: Name,
  description: Description,
  isActive: IsActive,
  configuration: {
    MinimumLongWorkTime,
    MinutesBetweenEvents,
    ContractTypeIds,
    LunchLength,
    BreakLength,
    AllowedDeviation,
    LunchJobId,
    LunchStart,
    BreakStart,
    ...otherConfig
  }
}) => {
  const timeFormat = 'H:mm:ss';

  const Configuration = {
    ...otherConfig,
    MinutesBetweenEvents: MinutesBetweenEvents
      ? parseInt(MinutesBetweenEvents, 10) * 60
      : MinutesBetweenEvents,
    ContractTypeIds: ContractTypeIds
      ? ContractTypeIds.map(Number)
      : ContractTypeIds,
    AllowedDeviation: AllowedDeviation
      ? parseInt(AllowedDeviation, 10)
      : AllowedDeviation,
    LunchJobId: LunchJobId
      ? parseInt(LunchJobId, 10)
      : LunchJobId,
    LunchLength: LunchLength
      ? parseInt(LunchLength, 10)
      : LunchLength,
    LunchStart: LunchStart
      ? dayjs(LunchStart, timeFormat).format(timeFormat)
      : LunchStart,
    BreakLength: BreakLength
      ? parseInt(BreakLength, 10)
      : BreakLength,
    BreakStart: BreakStart
      ? dayjs(BreakStart, timeFormat).format(timeFormat)
      : BreakStart,
    MinimumLongWorkTime: MinimumLongWorkTime
      ? dayjs(MinimumLongWorkTime, timeFormat).format(timeFormat)
      : MinimumLongWorkTime
  };

  return {
    Id,
    ParagraphId,
    Name,
    Description,
    IsActive,
    Configuration: JSON.stringify(Configuration)
  };
};
